import { WarningDiamondIcon } from "@xola/icons";
import { Checkbox, formatDate, Modal, SubmitButton } from "@xola/ui-kit";
import clsx from "clsx";
import {
    DateFormat,
    getCustomerName,
    getExperiencePurchaseItems,
    getPaymentLineItems,
    handleSuccessfulSubmission,
    isExperiencePurchaseItem,
    isMerchandiseItem,
    isOtherSellerPurchase,
    PurchaseItemStatus,
} from "common";
import { isNumber } from "lodash";
import pluralize from "pluralize";
import React, { ReactNode, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import {
    Event,
    Experience,
    ExperiencePurchaseItem,
    MerchandisePurchaseItem,
    Partner,
    Permission,
    Purchase,
    PurchaseItem,
} from "types";
import { api } from "../../api/api";
import { useProduct } from "../../hooks/resources/useProduct";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useResource } from "../../hooks/useResource";
import {
    getCancelableMerchandisePurchaseItems,
    getMerchandiseCancellationCounts,
} from "../../models/MerchandisePurchaseItem";
import { isPackage } from "../../models/Purchase";
import { isCreatedByPartner } from "../../models/PurchaseItem";
import { SecondaryButton } from "../Common/SecondaryButton";
import { getRefundAmount } from "../PurchaseModification/Refund/helpers";
import { refetchMerchandiseCatalogItems } from "@/components/PurchaseCreate/helpers/PurchaseCreateHelpers";
import { getSeller } from "@/utils/getSeller";

type CloseEventType = "skip" | "confirm";

interface CancelItemModalProps {
    purchase: Purchase;
    item?: ExperiencePurchaseItem | MerchandisePurchaseItem;
    isOpen: boolean;
    onClose: (type: CloseEventType, showRefundModal: boolean, newPurchase?: Purchase, events?: Event[]) => void;
    onConfirm?: () => void;
}

export const CancelPurchaseItemModal = ({ purchase, item, isOpen, onClose, onConfirm }: CancelItemModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={() => onClose("skip", false)}>
            <ModalContent purchase={purchase} item={item} onClose={onClose} onConfirm={onConfirm} />
        </Modal>
    );
};

interface SelectedItemInterface {
    item: ExperiencePurchaseItem | MerchandisePurchaseItem;
    removePartner: boolean | undefined | "";
}

interface GetCancelablePurchaseItemsProps {
    isMerchandise: boolean;
    item?: ExperiencePurchaseItem | MerchandisePurchaseItem;
    experienceItems: ExperiencePurchaseItem[];
    merchandiseItems: MerchandisePurchaseItem[];
    cancelAll: boolean;
}

export const getCancelablePurchaseItems = ({
    isMerchandise,
    item,
    experienceItems,
    merchandiseItems,
    cancelAll,
}: GetCancelablePurchaseItemsProps): Array<ExperiencePurchaseItem | MerchandisePurchaseItem> => {
    if (isMerchandise) {
        return merchandiseItems;
    }

    if (item) {
        return [item];
    }

    if (cancelAll) {
        return [...experienceItems, ...merchandiseItems];
    }

    return experienceItems;
};

const ModalContent = ({ purchase, item, onClose, onConfirm }: Except<CancelItemModalProps, "isOpen">) => {
    const [selectedItems, setSelectedItems] = useState<SelectedItemInterface[]>();
    const experienceItems = getExperiencePurchaseItems(purchase);
    const merchandiseItems = getCancelableMerchandisePurchaseItems(purchase);
    const isMerchandise = !!item && isMerchandiseItem(item);
    const cancelAll = !item && (experienceItems.length > 0 || merchandiseItems.length > 0);
    const allowItemSelection = isMerchandise && merchandiseItems.length > 1;
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const canRefund = isGranted(Permission.MODIFY_BOOKING);

    const { product, isLoadingProduct } = useProduct(item?.product.id ?? purchase.items[0].parentProduct?.id);

    const purchaseItems = getCancelablePurchaseItems({
        isMerchandise,
        item,
        experienceItems,
        merchandiseItems,
        cancelAll,
    });

    useEffect(() => {
        const experienceItems = getExperiencePurchaseItems(purchase);
        const merchandiseItems = getCancelableMerchandisePurchaseItems(purchase);
        const purchaseItems = getCancelablePurchaseItems({
            isMerchandise,
            item,
            experienceItems,
            merchandiseItems,
            cancelAll,
        });
        const selectedPurchaseItems =
            // if product is package and even single item is selected, need to cancel all experience items
            isPackage(product) && item ? experienceItems : purchaseItems;

        // Default select all items either for MIST or selected single item
        setSelectedItems(
            selectedPurchaseItems.map((item) => ({
                item: item,
                removePartner: true,
            })),
        );
    }, [cancelAll, isMerchandise, item, purchase, product]);

    const updateSelectedItems = ({ item, removePartner }: SelectedItemInterface) => {
        const updatedSelectedItems = selectedItems?.map((selectedItem) => {
            if (selectedItem.item.id === item.id) {
                return {
                    item: item,
                    removePartner: removePartner,
                };
            }

            return selectedItem;
        });
        setSelectedItems(updatedSelectedItems);
    };

    const toggleSelectedItem = ({ item, removePartner }: SelectedItemInterface) => {
        const isItemSelected = selectedItems?.some((selectedItem) => selectedItem.item.id === item.id);

        if (isItemSelected) {
            setSelectedItems(selectedItems?.filter((selectedItem) => selectedItem.item.id !== item.id));
            return;
        }

        setSelectedItems([...(selectedItems ?? []), { item, removePartner }]);
    };

    const invalidateEvents = (purchase: Purchase) => {
        for (const item of purchase.items) {
            if (item.event && item.status === PurchaseItemStatus.CANCELED) {
                invalidate("events", item.event.id);
            }
        }
    };

    const {
        mutateAsync: cancelPurchase,
        isLoading: isCanceling,
        isSuccess,
    } = useMutation(async () => {
        if (!selectedItems) {
            return null;
        }

        const params = { expand: ["event"] };
        let newPurchase: Purchase | undefined;
        for (const selectedItem of selectedItems) {
            const items = [
                {
                    id: selectedItem.item.id,
                    status: PurchaseItemStatus.CANCELED,
                    partner: selectedItem?.removePartner ? null : undefined,
                },
            ];
            const { data } = await api.patch<Purchase>(`/purchases/${purchase.id}`, { items }, { params });
            newPurchase = data;
        }

        if (!newPurchase) {
            return null;
        }

        const events: Event[] = [];
        for (const purchaseItem of newPurchase.items) {
            if (!isExperiencePurchaseItem(purchaseItem)) {
                continue;
            }

            const isUpdated = item ? purchaseItem.id === item.id : "start" in purchaseItem.event;
            if (isUpdated) {
                events.push(purchaseItem.event as Event);
            }
        }

        if (!isOtherSellerPurchase(newPurchase, getSeller().id)) {
            invalidateEvents(newPurchase);
        }

        const merchandiseProductIds = [
            ...new Set(
                selectedItems
                    .map((selectedItem) => selectedItem.item)
                    .filter((selectedItem): selectedItem is MerchandisePurchaseItem => isMerchandiseItem(selectedItem))
                    .map((selectedItem) => selectedItem.product.id),
            ),
        ];

        for (const productId of merchandiseProductIds) {
            await refetchMerchandiseCatalogItems(productId);
        }

        const maxRefundAmount = getRefundAmount(newPurchase);
        const isPayLater = getPaymentLineItems(newPurchase).length === 0;
        const isZeroPrice = isPayLater && purchase.amount === 0;

        handleSuccessfulSubmission(() => {
            onConfirm?.();
            onClose("confirm", canRefund && (maxRefundAmount > 0 || isPayLater) && !isZeroPrice, newPurchase, events);
        });
    });

    return (
        <>
            <Modal.Header description={cancelAll ? "This action will cancel the following reservations" : ""}>
                {isMerchandise ? "Cancel Merchandise" : cancelAll ? "Cancel All Reservations" : "Cancel Reservation"}
            </Modal.Header>

            <Modal.Body>
                {purchaseItems?.map((item) => (
                    <ModalBody
                        key={item.id}
                        item={item}
                        purchase={purchase}
                        isSelected={isSelected(item, selectedItems)}
                        cancelAll={cancelAll}
                        setSelectedItem={updateSelectedItems}
                        toggleSelectedItem={toggleSelectedItem}
                        allowSelection={allowItemSelection}
                    />
                ))}
            </Modal.Body>

            <Modal.Footer className="space-x-4">
                <SecondaryButton disabled={isCanceling || isLoadingProduct} onClick={() => onClose("skip", false)}>
                    Skip
                </SecondaryButton>

                <SubmitButton
                    isLoading={isCanceling}
                    isSuccess={isSuccess}
                    color="danger"
                    disabled={!selectedItems?.length}
                    onClick={cancelPurchase}
                >
                    Confirm
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};

interface ModalBodyProps {
    purchase: Purchase;
    item: ExperiencePurchaseItem | MerchandisePurchaseItem;
    setSelectedItem: (item: SelectedItemInterface) => void;
    toggleSelectedItem: (item: SelectedItemInterface) => void;
    isSelected: boolean;
    cancelAll: boolean;
    allowSelection: boolean;
}

const ModalBody = ({
    item,
    purchase,
    setSelectedItem,
    toggleSelectedItem,
    isSelected,
    cancelAll,
    allowSelection,
}: ModalBodyProps) => {
    const isMerchandise = isMerchandiseItem(item);
    const { product: experience } = useProduct<Experience>(item.product.id, null, {
        enabled: !isMerchandise,
    });

    const { isGranted } = useAuthorization();

    const { data: partner } = useResource<Partner>(["partners", item.partner?.id]);

    const canRemovePartner =
        !isMerchandise &&
        isExperiencePurchaseItem(item) &&
        item.partner &&
        isGranted(Permission.AFFILIATES_VIEW) &&
        !isCreatedByPartner(purchase, item, partner);

    const [removePartner, setRemovePartner] = useState(canRemovePartner);

    const handleCheckboxChange = (e: any) => {
        setRemovePartner(e.target.checked);
    };

    const handleItemClick = () => {
        if (allowSelection) {
            toggleSelectedItem({ item: item, removePartner: removePartner });
            return;
        }

        setSelectedItem({ item: item, removePartner: removePartner });
    };

    return (
        <div
            className={clsx(
                "m-1 rounded p-1",
                !cancelAll && "cursor-pointer",
                isSelected ? "bg-blue-lighter" : "hover:bg-secondary-lighter",
            )}
            onClick={cancelAll ? undefined : handleItemClick}
        >
            <p className="p-3">{getCancelWarning(cancelAll, purchase, item, experience)}</p>

            {canRemovePartner ? (
                <Checkbox
                    data-testid="cancel-purchase-item-modal-affiliate-checkbox"
                    checked={removePartner}
                    label="Remove partner discount, deposit and commission."
                    onChange={(e: any) => {
                        handleCheckboxChange(e);
                        setSelectedItem({ item, removePartner: e.target.checked });
                    }}
                />
            ) : null}
        </div>
    );
};

export const isSelected = (item: PurchaseItem, selectedItems?: SelectedItemInterface[]) => {
    return selectedItems?.findIndex((selectedItem) => selectedItem.item.id === item?.id) !== -1;
};

interface WarningWithIconProps {
    children: ReactNode;
}

const WarningWithIcon = ({ children }: WarningWithIconProps) => (
    <span className="inline-flex items-center gap-2">
        <WarningDiamondIcon className="flex-shrink-0 text-danger" />
        <span>{children}</span>
    </span>
);

const getCancelWarning = (cancelAll: boolean, purchase: Purchase, item: PurchaseItem, experience?: Experience) => {
    if (isMerchandiseItem(item)) {
        const merchandiseItem = item as MerchandisePurchaseItem;
        const customerName = getCustomerName(purchase);
        const customerNameText = customerName ? (
            <>
                for <var data-var="customerName">{customerName}</var>
            </>
        ) : null;

        const itemDetail = (
            <>
                <var data-var="itemName">{item.name}</var> {customerNameText}
            </>
        );

        const fulfilledItemDetails = <MerchandiseFulfillmentDetails merchandiseItem={merchandiseItem} />;

        const warning = <>Are you sure you want to cancel {itemDetail}?</>;

        const cancelAllDetail = (
            <>
                {itemDetail}
                {fulfilledItemDetails}
            </>
        );

        const warningWithDetails = (
            <>
                <WarningWithIcon>{warning}</WarningWithIcon>
                {fulfilledItemDetails}
            </>
        );

        return cancelAll ? cancelAllDetail : warningWithDetails;
    }

    // Handle experience items
    const experienceItem = item as ExperiencePurchaseItem;
    const { arrivalTime, arrivalDateTime } = experienceItem;

    if (experience) {
        const customerName = getCustomerName(purchase);

        const arrivalTimeText = isNumber(arrivalTime) ? (
            <>
                &nbsp;at <var data-var="arrivalTime">{formatDate(arrivalDateTime, DateFormat.TIME)}</var>
            </>
        ) : null;

        const customerNameText = customerName ? (
            <>
                for <var data-var="customerName">{customerName}</var>
            </>
        ) : null;

        const itemDetail = (
            <>
                <var data-var="experienceName">{experience.name}</var> on&nbsp;
                <var data-var="arrivalDate">{formatDate(arrivalDateTime, DateFormat.DATE_LONG)}</var>&nbsp;
                {arrivalTimeText}
            </>
        );

        const warning = (
            <>
                Are you sure you want to cancel {itemDetail} {customerNameText}?
            </>
        );

        return cancelAll ? itemDetail : <WarningWithIcon>{warning}</WarningWithIcon>;
    }
};

interface MerchandiseFulfillmentDetailsProps {
    merchandiseItem: MerchandisePurchaseItem;
}

const MerchandiseFulfillmentDetails = ({ merchandiseItem }: MerchandiseFulfillmentDetailsProps) => {
    const { willCancel, willReturn } = getMerchandiseCancellationCounts(merchandiseItem);

    return (
        <>
            <span className="block font-bold">
                <var data-var="cancelSummary">{`${willCancel} ${pluralize("item", willCancel)} will be cancelled`}</var>
            </span>
            <span className="block font-bold">
                <var data-var="returnSummary">{`${willReturn} ${pluralize("item", willReturn)} will be returned`}</var>
            </span>
        </>
    );
};
